import React from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  RefreshCw,
  Download,
  Search,
  AlertCircle,
  Mail,
  Phone,
  Building2,
  Inbox,
  BookOpen,
  Users,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GoldButton, SectionEyebrow } from "@/components/ui-bits";
import { ADMIN } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ogo_admin_token";

const SERVICE_FILTERS = [
  { value: "all", label: "All Services" },
  { value: "Mobile Notary", label: "Mobile Notary" },
  { value: "Remote Online Notary", label: "Remote Online Notary" },
  { value: "Loan Signing Agent", label: "Loan Signing Agent" },
  { value: "Apostille", label: "Apostille" },
  { value: "Wedding Officiant", label: "Wedding Officiant" },
  { value: "Elder Care Notary", label: "Elder Care Notary" },
  { value: "Other", label: "Other" },
];

const ROLE_FILTERS = [
  { value: "all", label: "All Roles" },
  { value: "Title Officer", label: "Title Officer" },
  { value: "Escrow Officer", label: "Escrow Officer" },
  { value: "Loan Officer / Mortgage Broker", label: "Loan Officer / Broker" },
  { value: "Signing Service Manager", label: "Signing Service Manager" },
  { value: "Real Estate Attorney", label: "Real Estate Attorney" },
  { value: "Other", label: "Other" },
];

const LEAD_FILTERS = [
  { value: "all", label: "All Sources" },
  { value: "apps_page", label: "Apps Page" },
  { value: "resources_page", label: "Resources Page" },
];

const MEMBER_FILTERS = [
  { value: "all", label: "All Members" },
];

const inputBase =
  "w-full bg-[#1A2B45] border border-white/10 text-white p-3 text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none transition-colors rounded-none placeholder:text-slate-500";

function formatErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return "Something went wrong.";
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return String(s);
  }
}

export default function AdminPage() {
  const [token, setToken] = React.useState(
    () => localStorage.getItem(TOKEN_KEY) || ""
  );
  const [verifying, setVerifying] = React.useState(!!token);

  // Verify existing token on mount
  React.useEffect(() => {
    let alive = true;
    if (!token) return;
    axios
      .get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => alive && setVerifying(false))
      .catch(() => {
        if (!alive) return;
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setVerifying(false);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (verifying) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-slate-400 text-sm uppercase tracking-[0.22em]">
          Verifying session…
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <LoginForm
        onSuccess={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  return <Dashboard token={token} onLogout={logout} />;
}

function LoginForm({ onSuccess }) {
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!password) {
      setError("Please enter the admin password.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      onSuccess(data.token);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-[80vh] flex items-center">
      <div className="max-w-md mx-auto w-full px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <SectionEyebrow>Admin Console</SectionEyebrow>
            <h1 className="font-serif-display text-3xl text-white mt-1">
              Sign in
            </h1>
          </div>
        </div>

        <form
          data-testid={ADMIN.loginForm}
          onSubmit={onSubmit}
          className="border border-white/10 bg-[#1A2B45] p-8 space-y-5"
          noValidate
        >
          {error && (
            <div
              data-testid={ADMIN.loginError}
              className="flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4"
            >
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-100">{error}</div>
            </div>
          )}

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-slate-400 mb-2">
              Admin Password
            </span>
            <input
              data-testid={ADMIN.loginPassword}
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputBase}
              placeholder="••••••••••••"
            />
          </label>

          <GoldButton
            type="submit"
            data-testid={ADMIN.loginSubmit}
            className={`w-full ${submitting ? "opacity-60 cursor-not-allowed" : ""}`}
            disabled={submitting}
          >
            {submitting ? "Signing in…" : "Sign In"}
          </GoldButton>

          <p className="text-xs text-slate-500 leading-relaxed">
            This area is restricted to OGO Notary staff. All sign-in attempts
            are logged.
          </p>
        </form>
      </div>
    </section>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = React.useState("contacts");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");

  const auth = React.useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const filters =
    tab === "contacts"
      ? SERVICE_FILTERS
      : tab === "vendors"
      ? ROLE_FILTERS
      : LEAD_FILTERS;
  const filterParam =
    tab === "contacts"
      ? "service"
      : tab === "vendors"
      ? "role"
      : tab === "members"
      ? null
      : "source";

  const fetchRows = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (filterParam && filter && filter !== "all") params.set(filterParam, filter);
      if (search.trim()) params.set("search", search.trim());
      const endpoint =
        tab === "contacts"
          ? "contacts"
          : tab === "vendors"
          ? "vendors"
          : tab === "members"
          ? "members"
          : "leads";
      const url = `${API}/admin/${endpoint}?${params}`;
      const { data } = await axios.get(url, auth);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      setError(formatErr(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [tab, filter, search, filterParam, auth, onLogout]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Reset search/filter when switching tabs
  React.useEffect(() => {
    setSearch("");
    setFilter("all");
  }, [tab]);

  const exportCsv = async () => {
    try {
      const resp = await axios.get(
        `${API}/admin/export.csv?type=${tab}`,
        { ...auth, responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        tab === "contacts"
          ? "ogo_contacts.csv"
          : tab === "vendors"
          ? "ogo_vendors.csv"
          : tab === "members"
          ? "ogo_members.csv"
          : "ogo_leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        formatErr(err?.response?.data?.detail) || "CSV export failed."
      );
    }
  };

  return (
    <section data-testid={ADMIN.dashboard} className="py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header strip */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <SectionEyebrow className="mb-3">Admin Console</SectionEyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight">
              Leads Dashboard
            </h1>
            <p className="mt-2 text-slate-400 text-sm max-w-xl leading-relaxed">
              Review and export contact requests, B2B vendor signups, and
              resource / app waitlist leads. Data is filtered server-side;
              CSV export includes the full collection.
            </p>
          </div>
          <button
            type="button"
            data-testid={ADMIN.logout}
            onClick={onLogout}
            className="self-start lg:self-end inline-flex items-center gap-2 border border-white/10 text-slate-300 hover:text-white hover:border-white/30 px-4 py-3 text-xs uppercase tracking-[0.22em]"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap border border-white/10 bg-[#1A2B45] mb-6 w-full sm:w-fit">
          <button
            type="button"
            data-testid={ADMIN.tabContacts}
            onClick={() => setTab("contacts")}
            className={`px-6 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 transition-colors ${
              tab === "contacts"
                ? "bg-[#D4AF37] text-[#142339] font-bold"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Inbox className="w-4 h-4" /> Contact Requests
          </button>
          <button
            type="button"
            data-testid={ADMIN.tabVendors}
            onClick={() => setTab("vendors")}
            className={`px-6 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 transition-colors ${
              tab === "vendors"
                ? "bg-[#D4AF37] text-[#142339] font-bold"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Building2 className="w-4 h-4" /> Vendor Signups
          </button>
          <button
            type="button"
            data-testid={ADMIN.tabLeads}
            onClick={() => setTab("leads")}
            className={`px-6 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 transition-colors ${
              tab === "leads"
                ? "bg-[#D4AF37] text-[#142339] font-bold"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <BookOpen className="w-4 h-4" /> Leads / Waitlist
          </button>
          <button
            type="button"
            data-testid="admin-tab-members"
            onClick={() => setTab("members")}
            className={`px-6 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 transition-colors ${
              tab === "members"
                ? "bg-[#D4AF37] text-[#142339] font-bold"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Users className="w-4 h-4" /> Members
          </button>
        </div>

        {/* Controls */}
        <div className="grid lg:grid-cols-12 gap-3 mb-6">
          <div className="lg:col-span-6 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              data-testid={ADMIN.search}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchRows()}
              placeholder={
                tab === "contacts"
                  ? "Search name, email, phone, message…"
                  : tab === "vendors"
                  ? "Search company, contact, email, notes…"
                  : tab === "members"
                  ? "Search member name or email…"
                  : "Search name, email, resource…"
              }
              className={`${inputBase} pl-10`}
            />
          </div>
          <div className="lg:col-span-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger
                data-testid={ADMIN.filterTrigger}
                className="bg-[#1A2B45] border-white/10 text-white p-3 h-auto text-sm rounded-none focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
              >
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent
                data-testid={ADMIN.filter}
                className="bg-[#1A2B45] border border-white/10 rounded-none text-white"
              >
                {filters.map((f) => (
                  <SelectItem
                    key={f.value}
                    value={f.value}
                    className="rounded-none focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                  >
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-3 grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid={ADMIN.refresh}
              onClick={fetchRows}
              className="inline-flex items-center justify-center gap-2 border border-white/10 text-slate-200 hover:border-white/30 px-4 py-3 text-xs uppercase tracking-[0.22em]"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              data-testid={ADMIN.exportCsv}
              onClick={exportCsv}
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-4 py-3 text-xs uppercase tracking-[0.22em] font-bold"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div className="text-sm text-slate-100">{error}</div>
          </div>
        )}

        {/* Table */}
        {tab === "contacts" ? (
          <ContactsTable rows={rows} loading={loading} />
        ) : tab === "vendors" ? (
          <VendorsTable rows={rows} loading={loading} />
        ) : tab === "members" ? (
          <MembersTable rows={rows} loading={loading} />
        ) : (
          <LeadsTable rows={rows} loading={loading} />
        )}

        <div className="mt-4 text-xs text-slate-500 tracking-wide">
          {loading
            ? "Loading…"
            : `${rows.length} ${rows.length === 1 ? "record" : "records"} shown`}
        </div>
      </div>
    </section>
  );
}

function EmptyState({ label }) {
  return (
    <div
      data-testid={ADMIN.empty}
      className="border border-dashed border-white/10 p-16 text-center text-slate-500"
    >
      <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-600" />
      {label}
    </div>
  );
}

function ContactsTable({ rows, loading }) {
  if (!loading && rows.length === 0)
    return <EmptyState label="No contact requests yet." />;
  return (
    <div className="overflow-x-auto border border-white/10">
      <table
        data-testid={ADMIN.contactsTable}
        className="w-full text-sm"
      >
        <thead className="bg-[#1A2B45] text-slate-400 uppercase text-[10px] tracking-[0.22em]">
          <tr>
            <Th>Date</Th>
            <Th>Name</Th>
            <Th>Contact</Th>
            <Th>Service</Th>
            <Th>Preferred</Th>
            <Th>Message</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10 bg-[#142339]">
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`admin-contact-row-${r.id}`}
              className="hover:bg-white/[0.02]"
            >
              <Td className="text-slate-400 whitespace-nowrap">
                {fmtDate(r.created_at)}
              </Td>
              <Td className="text-white font-medium">{r.full_name}</Td>
              <Td>
                <div className="flex flex-col gap-1">
                  <a
                    href={`mailto:${r.email}`}
                    className="inline-flex items-center gap-2 text-slate-200 hover:text-[#D4AF37]"
                  >
                    <Mail className="w-3 h-3" /> {r.email}
                  </a>
                  <a
                    href={`tel:${r.phone}`}
                    className="inline-flex items-center gap-2 text-slate-400 hover:text-[#D4AF37]"
                  >
                    <Phone className="w-3 h-3" /> {r.phone}
                  </a>
                </div>
              </Td>
              <Td>
                <span className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37] px-2 py-1 text-[10px] uppercase tracking-[0.22em]">
                  {r.service}
                </span>
              </Td>
              <Td className="text-slate-400">{r.preferred_datetime || "—"}</Td>
              <Td className="text-slate-300 max-w-md">
                <div className="line-clamp-3">{r.message}</div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VendorsTable({ rows, loading }) {
  if (!loading && rows.length === 0)
    return <EmptyState label="No vendor signups yet." />;
  return (
    <div className="overflow-x-auto border border-white/10">
      <table
        data-testid={ADMIN.vendorsTable}
        className="w-full text-sm"
      >
        <thead className="bg-[#1A2B45] text-slate-400 uppercase text-[10px] tracking-[0.22em]">
          <tr>
            <Th>Date</Th>
            <Th>Company</Th>
            <Th>Contact</Th>
            <Th>Role</Th>
            <Th>Volume</Th>
            <Th>Coverage</Th>
            <Th>Notes</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10 bg-[#142339]">
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`admin-vendor-row-${r.id}`}
              className="hover:bg-white/[0.02]"
            >
              <Td className="text-slate-400 whitespace-nowrap">
                {fmtDate(r.created_at)}
              </Td>
              <Td className="text-white font-medium">
                <div>{r.company_name}</div>
                {r.website && (
                  <a
                    href={r.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-slate-500 hover:text-[#D4AF37]"
                  >
                    {r.website}
                  </a>
                )}
              </Td>
              <Td>
                <div className="text-slate-200">{r.contact_name}</div>
                <div className="flex flex-col gap-1 mt-1">
                  <a
                    href={`mailto:${r.email}`}
                    className="inline-flex items-center gap-2 text-slate-400 text-xs hover:text-[#D4AF37]"
                  >
                    <Mail className="w-3 h-3" /> {r.email}
                  </a>
                  <a
                    href={`tel:${r.phone}`}
                    className="inline-flex items-center gap-2 text-slate-400 text-xs hover:text-[#D4AF37]"
                  >
                    <Phone className="w-3 h-3" /> {r.phone}
                  </a>
                </div>
              </Td>
              <Td>
                <span className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37] px-2 py-1 text-[10px] uppercase tracking-[0.22em]">
                  {r.role}
                </span>
              </Td>
              <Td className="text-slate-400">{r.expected_volume || "—"}</Td>
              <Td className="text-slate-400">{r.coverage_area || "—"}</Td>
              <Td className="text-slate-300 max-w-md">
                <div className="line-clamp-3">{r.notes || "—"}</div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 font-semibold">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-4 align-top ${className}`}>{children}</td>;
}

function LeadsTable({ rows, loading }) {
  if (!loading && rows.length === 0)
    return <EmptyState label="No leads yet." />;
  return (
    <div className="overflow-x-auto border border-white/10">
      <table
        data-testid={ADMIN.leadsTable}
        className="w-full text-sm"
      >
        <thead className="bg-[#1A2B45] text-slate-400 uppercase text-[10px] tracking-[0.22em]">
          <tr>
            <Th>Date</Th>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Resource / Interest</Th>
            <Th>Source</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10 bg-[#142339]">
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`admin-lead-row-${r.id}`}
              className="hover:bg-white/[0.02]"
            >
              <Td className="text-slate-400 whitespace-nowrap">
                {fmtDate(r.created_at)}
              </Td>
              <Td className="text-white font-medium">{r.name}</Td>
              <Td>
                <a
                  href={`mailto:${r.email}`}
                  className="inline-flex items-center gap-2 text-slate-200 hover:text-[#D4AF37]"
                >
                  <Mail className="w-3 h-3" /> {r.email}
                </a>
              </Td>
              <Td>
                <span className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37] px-2 py-1 text-[10px] uppercase tracking-[0.22em]">
                  {r.resource}
                </span>
              </Td>
              <Td className="text-slate-400 text-xs uppercase tracking-[0.18em]">
                {r.source || "—"}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MembersTable({ rows, loading }) {
  if (!loading && rows.length === 0)
    return <EmptyState label="No community members yet." />;
  return (
    <div className="overflow-x-auto border border-white/10">
      <table
        data-testid="admin-members-table"
        className="w-full text-sm"
      >
        <thead className="bg-[#1A2B45] text-slate-400 uppercase text-[10px] tracking-[0.22em]">
          <tr>
            <Th>Joined</Th>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Sign-in Method</Th>
            <Th>Downloads</Th>
            <Th>Last Download</Th>
            <Th>Last Login</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10 bg-[#142339]">
          {rows.map((r) => (
            <tr
              key={r.user_id}
              data-testid={`admin-member-row-${r.user_id}`}
              className="hover:bg-white/[0.02]"
            >
              <Td className="text-slate-400 whitespace-nowrap">
                {fmtDate(r.created_at)}
              </Td>
              <Td className="text-white font-medium">{r.name || "—"}</Td>
              <Td>
                <a
                  href={`mailto:${r.email}`}
                  className="inline-flex items-center gap-2 text-slate-200 hover:text-[#D4AF37]"
                >
                  <Mail className="w-3 h-3" /> {r.email}
                </a>
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {(r.auth_providers || []).map((p) => (
                    <span
                      key={p}
                      className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37] px-2 py-1 text-[10px] uppercase tracking-[0.22em]"
                    >
                      {p === "password" ? "Email" : p === "google" ? "Google" : p}
                    </span>
                  ))}
                </div>
              </Td>
              <Td className="text-white font-bold text-center">
                {r.download_count ?? 0}
              </Td>
              <Td className="text-slate-400 whitespace-nowrap">
                {r.last_download_at ? fmtDate(r.last_download_at) : "—"}
              </Td>
              <Td className="text-slate-400 whitespace-nowrap">
                {r.last_login_at ? fmtDate(r.last_login_at) : "—"}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
